import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { Star } from "@/components/Annotation";
import { Tape, StickyNote } from "@/components/Notebook";
import { TextCTA } from "@/components/CTA";
import { ROUTES, BRAND } from "@/config/links";
import { useLang } from "@/i18n";

export const About = () => {
  const { t } = useLang();
  return (
    <section id="about" className="relative z-10 py-24 md:py-36 bg-paper-deep/40" data-testid="about-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, y: 24, rotate: -2 }}
            whileInView={{ opacity: 1, y: 0, rotate: -2 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative bg-paper border border-ink/12 shadow-[6px_10px_40px_rgba(18,20,24,0.10)] p-4 max-w-sm mx-auto lg:mx-0"
          >
            <Tape className="absolute -top-4 left-8" rotate={-7} width={120} />
            <Tape className="absolute -top-4 right-8" rotate={6} width={120} />
            <div className="aspect-[4/5] bg-paper-deep/40 border border-ink/10 overflow-hidden text-ink">
              <img
                src="/sketches/coach-portrait.webp"
                srcSet="/sketches/coach-portrait-500.webp 500w, /sketches/coach-portrait.webp 900w"
                sizes="(min-width: 1024px) 30vw, 70vw"
                loading="lazy"
                alt={t("about.portrait_alt", { name: BRAND.coach })}
                draggable={false}
                data-testid="about-coach-portrait"
                className="w-full h-full object-cover object-[center_top] select-none"
                style={{ filter: "contrast(1.05)", mixBlendMode: "multiply" }}
              />
            </div>
            <div className="flex items-center justify-between px-1 pt-3">
              <span className="font-hand text-terracotta text-xl">{t("landing.coach_portrait_caption")}</span>
              <span className="font-body text-[10px] uppercase tracking-[0.16em] text-pencil">{t("landing.coach_portrait_tag")}</span>
            </div>
          </motion.div>

          <StickyNote className="hidden md:block absolute -bottom-6 -right-3 md:-right-6 w-40 z-20" rotate={4}>
            theatre-trained · communication coach
          </StickyNote>
          <Star className="absolute -top-5 -left-4 w-8 h-8" />
        </div>

        <div className="lg:col-span-7">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">{t("landing.coach_eyebrow")}</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink mb-6">
              {t("landing.coach_h")}
            </h2>
            <p className="hidden md:block font-body text-base md:text-lg text-ink-soft leading-relaxed mb-5 max-w-2xl" data-testid="coach-bio-desktop">
              {t("landing.coach_bio")}
            </p>
            <p className="md:hidden font-body text-ink-soft leading-relaxed mb-5" data-testid="coach-bio-mobile">
              {t("landing.coach_bio_mobile")}
            </p>

            <p className="hidden md:block font-body text-ink-soft leading-relaxed mb-5 max-w-2xl" data-testid="coach-business-desktop">
              {t("landing.coach_business")}
            </p>
            <p className="md:hidden font-body text-ink-soft leading-relaxed mb-5" data-testid="coach-business-mobile">
              {t("landing.coach_business_mobile")}
            </p>
          </Reveal>

          <Reveal delay={0.05} className="border-l-2 border-terracotta pl-5 max-w-xl mb-8">
            <p className="hidden md:block font-body text-ink leading-relaxed" data-testid="coach-supporting-desktop">
              {t("landing.coach_supporting")}
            </p>
            <p className="md:hidden font-body text-ink leading-relaxed" data-testid="coach-supporting-mobile">
              {t("landing.coach_supporting_mobile")}
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="font-body text-sm text-ink-soft mb-6" data-testid="coach-creds">
              {t("landing.coach_creds")}
            </p>
            <TextCTA href={ROUTES.about} testId="about-process-cta">
              {t("landing.coach_cta")}
            </TextCTA>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
